// Contexte global pour le Panier et les Favoris
// Synchronisé avec les APIs backend

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

// Contexte
const BuyerContext = createContext(null);

// Helper pour récupérer le token
const getToken = () => localStorage.getItem('buyer_token');

// Helper pour les headers
const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// API Fetch helper
const apiFetch = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }

  return response.json();
};

// Provider
export function BuyerProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [cartSubtotal, setCartSubtotal] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const token = getToken();
    const userStr = localStorage.getItem('buyer_user');
    
    if (token && userStr) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userStr));
      loadCartFromAPI();
      loadFavoritesFromAPI();
    } else {
      // Charger depuis localStorage pour les utilisateurs non connectés
      loadCartFromLocalStorage();
      loadFavoritesFromLocalStorage();
    }
    setLoading(false);
  }, []);

  // ============ LOCAL STORAGE (utilisateurs non connectés) ============

  const loadCartFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('guest_cart');
      if (saved) {
        const items = JSON.parse(saved);
        setCart(items);
        calculateTotals(items);
      }
    } catch (e) {
      console.error('Error loading cart from localStorage:', e);
    }
  };

  const loadFavoritesFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('guest_favorites');
      if (saved) {
        setFavorites(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading favorites from localStorage:', e);
    }
  };

  const saveCartToLocalStorage = (items) => {
    try {
      localStorage.setItem('guest_cart', JSON.stringify(items));
    } catch (e) {
      console.error('Error saving cart to localStorage:', e);
    }
  };

  const saveFavoritesToLocalStorage = (items) => {
    try {
      localStorage.setItem('guest_favorites', JSON.stringify(items));
    } catch (e) {
      console.error('Error saving favorites to localStorage:', e);
    }
  };

  // ============ API (utilisateurs connectés) ============

  const loadCartFromAPI = async () => {
    try {
      const data = await apiFetch('/api/me/cart');
      const items = data.items || [];
      setCart(items.map(item => ({
        id: item.product_id,
        itemId: item.id,
        title: item.product_name,
        price: item.product_price,
        image: item.product_image,
        quantity: item.quantity,
        seller_id: item.seller_id,
        seller_name: item.seller_name
      })));
      setCartSubtotal(data.subtotal || 0);
      setShippingCost(data.shipping_cost || 0);
      setCartTotal(data.total || 0);
    } catch (e) {
      console.error('Error loading cart from API:', e);
      // Fallback to localStorage
      loadCartFromLocalStorage();
    }
  };

  const loadFavoritesFromAPI = async () => {
    try {
      const data = await apiFetch('/api/me/favorites');
      setFavorites(data.map(item => ({
        id: item.product_id,
        title: item.product_name,
        price: item.product_price,
        image: item.product_image,
        seller_id: item.seller_id,
        seller_name: item.seller_name
      })));
    } catch (e) {
      console.error('Error loading favorites from API:', e);
      // Fallback to localStorage
      loadFavoritesFromLocalStorage();
    }
  };

  // ============ CALCULS ============

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shipping = subtotal >= 50 ? 0 : 5.99;
    setCartSubtotal(subtotal);
    setShippingCost(shipping);
    setCartTotal(subtotal + shipping);
  };

  // ============ ACTIONS PANIER ============

  const addToCart = useCallback(async (product, quantity = 1) => {
    const productId = product.id || product._id || product.product_id;
    
    // Extraire le nom du vendeur correctement (peut être un objet ou une string)
    const getSellerName = () => {
      if (product.seller_name) {
        return typeof product.seller_name === 'object' ? product.seller_name.name : product.seller_name;
      }
      if (product.seller) {
        return typeof product.seller === 'object' ? product.seller.name : product.seller;
      }
      return 'AbracadaBati';
    };
    
    const productData = {
      id: productId,
      title: product.title || product.name || product.product_name,
      price: product.price || product.product_price,
      image: product.image || product.photos?.[0] || product.product_image || null,
      seller_id: product.seller_id || product.seller?.id || 'default-seller',
      seller_name: getSellerName()
    };

    if (isAuthenticated) {
      try {
        await apiFetch('/api/me/cart/items', {
          method: 'POST',
          body: JSON.stringify({
            product_id: productId,
            product_name: productData.title,
            product_price: productData.price,
            product_image: productData.image,
            seller_id: productData.seller_id,
            seller_name: productData.seller_name,
            quantity
          })
        });
        await loadCartFromAPI();
        toast.success(`${productData.title} ajouté au panier`);
      } catch (e) {
        toast.error('Erreur lors de l\'ajout au panier');
      }
    } else {
      // Mode local pour utilisateurs non connectés
      setCart(prev => {
        const existing = prev.find(item => item.id === productId);
        let newCart;
        if (existing) {
          newCart = prev.map(item => 
            item.id === productId 
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        } else {
          newCart = [...prev, { ...productData, quantity }];
        }
        saveCartToLocalStorage(newCart);
        calculateTotals(newCart);
        return newCart;
      });
      toast.success(`${productData.title} ajouté au panier`);
    }
  }, [isAuthenticated]);

  const updateCartQuantity = useCallback(async (productId, delta) => {
    const item = cart.find(i => i.id === productId);
    if (!item) return;

    const newQuantity = item.quantity + delta;
    
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    if (isAuthenticated && item.itemId) {
      try {
        await apiFetch(`/api/me/cart/items/${item.itemId}`, {
          method: 'PUT',
          body: JSON.stringify({ quantity: newQuantity })
        });
        await loadCartFromAPI();
      } catch (e) {
        toast.error('Erreur lors de la mise à jour');
      }
    } else {
      setCart(prev => {
        const newCart = prev.map(i => 
          i.id === productId ? { ...i, quantity: newQuantity } : i
        );
        saveCartToLocalStorage(newCart);
        calculateTotals(newCart);
        return newCart;
      });
    }
  }, [cart, isAuthenticated]);

  const removeFromCart = useCallback(async (productId) => {
    const item = cart.find(i => i.id === productId);
    
    if (isAuthenticated && item?.itemId) {
      try {
        await apiFetch(`/api/me/cart/items/${item.itemId}`, { method: 'DELETE' });
        await loadCartFromAPI();
        toast.success('Article supprimé du panier');
      } catch (e) {
        toast.error('Erreur lors de la suppression');
      }
    } else {
      setCart(prev => {
        const newCart = prev.filter(i => i.id !== productId);
        saveCartToLocalStorage(newCart);
        calculateTotals(newCart);
        return newCart;
      });
      toast.success('Article supprimé du panier');
    }
  }, [cart, isAuthenticated]);

  const clearCart = useCallback(async () => {
    if (isAuthenticated) {
      try {
        await apiFetch('/api/me/cart', { method: 'DELETE' });
        setCart([]);
        setCartSubtotal(0);
        setShippingCost(0);
        setCartTotal(0);
        toast.success('Panier vidé');
      } catch (e) {
        toast.error('Erreur lors du vidage du panier');
      }
    } else {
      setCart([]);
      setCartSubtotal(0);
      setShippingCost(0);
      setCartTotal(0);
      localStorage.removeItem('guest_cart');
      toast.success('Panier vidé');
    }
  }, [isAuthenticated]);

  // ============ ACTIONS FAVORIS ============

  const addToFavorites = useCallback(async (product) => {
    const productId = product.id || product._id || product.product_id;
    const productData = {
      id: productId,
      title: product.title || product.name || product.product_name,
      price: product.price || product.product_price,
      image: product.image || product.photos?.[0] || product.product_image || null,
      seller_id: product.seller_id || 'default-seller',
      seller_name: product.seller || product.seller_name || 'AbracadaBati'
    };

    if (isAuthenticated) {
      try {
        await apiFetch('/api/me/favorites', {
          method: 'POST',
          body: JSON.stringify({
            product_id: productId,
            product_name: productData.title,
            product_price: productData.price,
            product_image: productData.image,
            seller_id: productData.seller_id,
            seller_name: productData.seller_name
          })
        });
        await loadFavoritesFromAPI();
        toast.success(`${productData.title} ajouté aux favoris`);
      } catch (e) {
        if (!e.message.includes('déjà')) {
          toast.error('Erreur lors de l\'ajout aux favoris');
        }
      }
    } else {
      setFavorites(prev => {
        if (prev.find(f => f.id === productId)) {
          return prev;
        }
        const newFavorites = [...prev, productData];
        saveFavoritesToLocalStorage(newFavorites);
        return newFavorites;
      });
      toast.success(`${productData.title} ajouté aux favoris`);
    }
  }, [isAuthenticated]);

  const removeFromFavorites = useCallback(async (productId) => {
    if (isAuthenticated) {
      try {
        await apiFetch(`/api/me/favorites/${productId}`, { method: 'DELETE' });
        await loadFavoritesFromAPI();
        toast.success('Produit retiré des favoris');
      } catch (e) {
        toast.error('Erreur lors de la suppression');
      }
    } else {
      setFavorites(prev => {
        const newFavorites = prev.filter(f => f.id !== productId);
        saveFavoritesToLocalStorage(newFavorites);
        return newFavorites;
      });
      toast.success('Produit retiré des favoris');
    }
  }, [isAuthenticated]);

  const toggleFavorite = useCallback(async (product) => {
    const productId = product.id || product._id || product.product_id;
    const isFav = favorites.some(f => f.id === productId);
    
    if (isFav) {
      await removeFromFavorites(productId);
    } else {
      await addToFavorites(product);
    }
  }, [favorites, addToFavorites, removeFromFavorites]);

  const isFavorite = useCallback((productId) => {
    return favorites.some(f => f.id === productId);
  }, [favorites]);

  // ============ AUTH ============

  const login = useCallback(async (email, password) => {
    const response = await fetch(`${API_URL}/api/client/auth/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Identifiants incorrects');
    }
    
    const data = await response.json();
    localStorage.setItem('buyer_token', data.token);
    localStorage.setItem('buyer_user', JSON.stringify(data.user));
    setIsAuthenticated(true);
    setUser(data.user);
    
    // Synchroniser le panier local vers l'API
    if (cart.length > 0) {
      for (const item of cart) {
        try {
          await apiFetch('/api/me/cart/items', {
            method: 'POST',
            body: JSON.stringify({
              product_id: item.id,
              product_name: item.title,
              product_price: item.price,
              product_image: item.image,
              seller_id: item.seller_id || 'default-seller',
              seller_name: item.seller_name || 'AbracadaBati',
              quantity: item.quantity
            })
          });
        } catch (e) {
          console.error('Error syncing cart item:', e);
        }
      }
      localStorage.removeItem('guest_cart');
    }
    
    // Synchroniser les favoris locaux vers l'API
    if (favorites.length > 0) {
      for (const item of favorites) {
        try {
          await apiFetch('/api/me/favorites', {
            method: 'POST',
            body: JSON.stringify({
              product_id: item.id,
              product_name: item.title,
              product_price: item.price,
              product_image: item.image,
              seller_id: item.seller_id || 'default-seller',
              seller_name: item.seller_name || 'AbracadaBati'
            })
          });
        } catch (e) {
          console.error('Error syncing favorite:', e);
        }
      }
      localStorage.removeItem('guest_favorites');
    }
    
    // Recharger depuis l'API
    await loadCartFromAPI();
    await loadFavoritesFromAPI();
    
    return data;
  }, [cart, favorites]);

  const logout = useCallback(() => {
    localStorage.removeItem('buyer_token');
    localStorage.removeItem('buyer_user');
    setIsAuthenticated(false);
    setUser(null);
    setCart([]);
    setFavorites([]);
    setCartTotal(0);
    setCartSubtotal(0);
    setShippingCost(0);
    toast.success('Déconnexion réussie');
  }, []);

  const value = {
    // État
    cart,
    favorites,
    cartTotal,
    cartSubtotal,
    shippingCost,
    cartCount: cart.reduce((sum, item) => sum + item.quantity, 0),
    favoritesCount: favorites.length,
    isAuthenticated,
    user,
    loading,
    
    // Actions Panier
    addToCart,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    
    // Actions Favoris
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    
    // Auth
    login,
    logout,
    
    // Refresh
    refreshCart: loadCartFromAPI,
    refreshFavorites: loadFavoritesFromAPI
  };

  return (
    <BuyerContext.Provider value={value}>
      {children}
    </BuyerContext.Provider>
  );
}

// Hook pour utiliser le contexte
export function useBuyer() {
  const context = useContext(BuyerContext);
  if (!context) {
    throw new Error('useBuyer must be used within a BuyerProvider');
  }
  return context;
}

export default BuyerContext;
